import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getGMBAccessToken } from "../services/gmbAuthentication";
import {
  fetchGMBAccounts,
  fetchLocationsByAccount,
  checkConnectedLocations,
} from "../services/gmbLocations";
import { GMBLocation, CustomerOption } from "../types/gmb.types";
import { getAllCustomers } from "../../../../../services/customerService";
import { useAuth } from "../../../../../context/AuthContext";
import { debug } from "../../../../../utils/debugLogger";
import { validateOAuthState } from "../../../../../utils/oauthState";

export function useGMBConnection() {
  const location = useLocation();
  const { idToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string>("");
  const [refreshToken, setRefreshToken] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [locations, setLocations] = useState<GMBLocation[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  useEffect(() => {
    const fetchTokensAndLocations = async () => {
      try {
        // Extract code from URL
        const urlParams = new URLSearchParams(location.search);
        if (!validateOAuthState("gmb", urlParams.get("state"))) {
          throw new Error("Security check failed (invalid OAuth state). Please try connecting again.");
        }
        const code = urlParams.get("code");

        if (!code) {
          throw new Error("Authorization code not found in URL");
        }

        // Exchange code for tokens
        const tokenData = await getGMBAccessToken(code);
        setAccessToken(tokenData.access_token);
        setRefreshToken(tokenData.refresh_token);
        setEmail(tokenData.email);

        // Get all accounts
        const accounts = await fetchGMBAccounts(tokenData.access_token);

        // Get locations for all accounts
        const locationPromises = accounts.map((account) =>
          fetchLocationsByAccount(tokenData.access_token, account.name)
        );

        const locationsArrays = await Promise.all(locationPromises);
        let allLocations = locationsArrays.flat();

        // Check which locations are already connected
        allLocations = await checkConnectedLocations(allLocations);

        setLocations(allLocations);

        // Get customer list using the existing service
        if (idToken) {
          const customersList = await getAllCustomers(idToken);
          // Map to CustomerOption format
          setCustomers(
            customersList.map((customer) => ({
              customer_id: customer.customer_id,
              name: customer.business_name || customer.customer_id,
            }))
          );
        } else {
          setError("No authentication token available. Please log in again.");
        }

        setLoading(false);
      } catch (err) {
        debug.error("Error in GMB callback:", err);
        setError("Failed to connect to Google My Business. Please try again.");
        setLoading(false);
      }
    };

    fetchTokensAndLocations();
  }, [location.search, idToken]);

  return {
    loading,
    error,
    accessToken,
    refreshToken,
    email,
    locations,
    customers,
  };
}
